import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import uldefault from './image/uldefault.svg';
import ulcircle from './image/ulcircle.svg';
import ulsquare from './image/ulsquare.svg';
import oldefault from './image/list-ordered.svg';
import loweralpha from './image/loweralpha.svg';
import lowerroman from './image/lowerroman.svg';
import upperalpha from './image/upperalpha.svg';
import upperroman from './image/upperroman.svg';
import downtrangle from './image/downtrangle.svg';
import { Popover, PopoverTrigger, PopoverContent } from '@leadsnavi/ui-kit';
import * as EditorTypeAll from '../tinymce';
import {
  DROPDOWN_ARROW_STYLE,
  DROPDOWN_MENU_ITEM_STYLE,
  DROPDOWN_MENU_WRAPPER_STYLE,
  DROPDOWN_TRIGGER_STYLE,
} from './ThemeConstant';

interface Props {
  editorInstance: EditorTypeAll.Editor;
}

const defaultUlItems = [
  { value: '', icon: uldefault },
  { value: 'circle', icon: ulcircle },
  { value: 'square', icon: ulsquare },
];
const defaultOlItems = [
  { value: '', icon: oldefault  },
  { value: 'lower-alpha', icon: loweralpha },
  { value: 'lower-roman', icon: lowerroman },
  { value: 'upper-alpha', icon: upperalpha },
  { value: 'upper-roman', icon: upperroman },
];

export const ULLists = ({ editorInstance }: Props) => {
  const btnRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [selectUL, setSelectUL] = useState<string | null>(null);
  const [isULActive, setIsULActive] = useState(false);

  useEffect(() => {
    if (editorInstance) {
      editorInstance.on('NodeChange', () => {
        const isInUL = editorInstance.queryCommandState('InsertUnorderedList');
        setIsULActive(isInUL);

        // 根据编辑器当前状态更新选中的列表样式
        if (isInUL) {
          const node = editorInstance.selection.getNode();
          const listElement = editorInstance.dom.getParent(node, 'ul');
          if (listElement) {
            const listStyle = editorInstance.dom.getStyle(listElement, 'list-style-type');
            // 根据 list-style-type 找到对应的图标
            const matchedItem = defaultUlItems.find(item => item.value === (listStyle || ''));
            setSelectUL(matchedItem ? matchedItem.icon : uldefault);
          } else {
            setSelectUL(uldefault);
          }
        } else {
          setSelectUL(null);
        }
      });
    }
  }, [editorInstance]);

  const selectULCl = (item: { icon: string; value: string }) => {
    if (!editorInstance) return;
    
    // 检查当前是否已经在 UL 列表中
    const isInUL = editorInstance.queryCommandState('InsertUnorderedList');
    
    if (isInUL) {
      // 获取当前列表的样式
      const node = editorInstance.selection.getNode();
      const listElement = editorInstance.dom.getParent(node, 'ul');
      let currentListStyle = '';
      
      if (listElement) {
        currentListStyle = editorInstance.dom.getStyle(listElement, 'list-style-type') || '';
      }
      
      // 如果点击的样式和当前样式相同，则取消列表
      if (currentListStyle === item.value) {
        editorInstance.execCommand('InsertUnorderedList'); // 取消列表
        setSelectUL(null);
      } else {
        // 如果样式不同，直接更改样式而不取消列表
        if (listElement) {
          editorInstance.dom.setStyle(listElement, 'list-style-type', item.value);
        }
        setSelectUL(item.icon);
      }
    } else {
      // 如果不在列表中，创建新列表
      editorInstance.execCommand('InsertUnorderedList', false, { 'list-style-type': item.value });
      setSelectUL(item.icon);
    }
    
    setVisible(false);
  };

  const ulActionCompact = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation(); // 阻止触发 Popover 打开
    // 如果当前已经在列表中，则切换列表状态
    if (isULActive) {
      editorInstance.execCommand('InsertUnorderedList');
    } else {
      // 如果不在列表中，插入默认样式的列表
      editorInstance.execCommand('InsertUnorderedList', false, { 'list-style-type': '' });
    }
  };

  return (
    <Popover open={visible} onOpenChange={setVisible} modal={true}>
      <div ref={btnRef} className={classnames(['inline-flex'])}>
        {/* 主按钮：点击执行列表命令 */}
        <span
          className={classnames(...DROPDOWN_TRIGGER_STYLE, isULActive && 'bg-[#E5E5E5] hover:bg-[#E5E5E5]')}
          onClick={ulActionCompact}
        >
          <img width="18px" height="18px" src={selectUL || uldefault} />
        </span>

        {/* 下拉箭头：点击打开菜单 */}
        <PopoverTrigger asChild>
          <span className={classnames(...DROPDOWN_ARROW_STYLE)}>
            <img width="6px" height="18px" src={downtrangle} />
          </span>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className={classnames(...DROPDOWN_MENU_WRAPPER_STYLE)}
        side="top"
        sideOffset={5}
        style={{ zIndex: 9999 }}
      >
        <div className="text-xs">
          <div className="p-0.5">
            {defaultUlItems.map((item, index) => (
              <div
                key={index}
                className={classnames([
                  ...DROPDOWN_MENU_ITEM_STYLE,
                  item.icon === selectUL && isULActive && 'bg-[#E5E5E5]',
                ])}
                onClick={() => {
                  selectULCl(item);
                }}
              >
                <img width="20px" height="20px" src={item.icon} />
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const OLLists = ({ editorInstance }: Props) => {
  const btnRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [selectOL, setSelectOL] = useState<string | null>(null);
  const [isOLActive, setIsOLActive] = useState(false);

  useEffect(() => {
    if (editorInstance) {
      editorInstance.on('NodeChange', () => {
        const isInOL = editorInstance.queryCommandState('InsertOrderedList');
        setIsOLActive(isInOL);

        // 根据编辑器当前状态更新选中的列表样式
        if (isInOL) {
          const node = editorInstance.selection.getNode();
          const listElement = editorInstance.dom.getParent(node, 'ol');
          if (listElement) {
            const listStyle = editorInstance.dom.getStyle(listElement, 'list-style-type');
            // 根据 list-style-type 找到对应的图标
            const matchedItem = defaultOlItems.find(item => item.value === (listStyle || ''));
            setSelectOL(matchedItem ? matchedItem.icon : oldefault);
          } else {
            setSelectOL(oldefault);
          }
        } else {
          setSelectOL(null);
        }
      });
    }
  }, [editorInstance]);

  const selectOLCl = (item: { icon: string; value: string }) => {
    if (!editorInstance) return;
    
    // 检查当前是否已经在 OL 列表中
    const isInOL = editorInstance.queryCommandState('InsertOrderedList');
    
    if (isInOL) {
      // 获取当前列表的样式
      const node = editorInstance.selection.getNode();
      const listElement = editorInstance.dom.getParent(node, 'ol');
      let currentListStyle = '';
      
      if (listElement) {
        currentListStyle = editorInstance.dom.getStyle(listElement, 'list-style-type') || '';
      }
      
      // 如果点击的样式和当前样式相同，则取消列表
      if (currentListStyle === item.value) {
        editorInstance.execCommand('InsertOrderedList'); // 取消列表
        setSelectOL(null);
      } else {
        // 如果样式不同，直接更改样式而不取消列表
        if (listElement) {
          editorInstance.dom.setStyle(listElement, 'list-style-type', item.value);
        }
        setSelectOL(item.icon);
      }
    } else {
      // 如果不在列表中，创建新列表
      editorInstance.execCommand('InsertOrderedList', false, { 'list-style-type': item.value });
      setSelectOL(item.icon);
    }
    
    setVisible(false);
  };

  const olActionCompact = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation(); // 阻止触发 Popover 打开
    // 如果当前已经在列表中，则切换列表状态
    if (isOLActive) {
      editorInstance.execCommand('InsertOrderedList');
    } else {
      // 如果不在列表中，插入默认样式的列表
      editorInstance.execCommand('InsertOrderedList', false, { 'list-style-type': '' });
    }
  };

  return (
    <Popover open={visible} onOpenChange={setVisible} modal={true}>
      <div ref={btnRef} className={classnames(['inline-flex'])}>
        {/* 主按钮：点击执行列表命令 */}
        <span
          className={classnames(...DROPDOWN_TRIGGER_STYLE, isOLActive && 'bg-[#E5E5E5] hover:bg-[#E5E5E5]')}
          onClick={olActionCompact}
        >
          <img width="18px" height="18px" src={selectOL || oldefault} />
        </span>

        {/* 下拉箭头：点击打开菜单 */}
        <PopoverTrigger asChild>
          <span className={classnames(...DROPDOWN_ARROW_STYLE)}>
            <img width="6px" height="18px" src={downtrangle} />
          </span>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className={classnames(...DROPDOWN_MENU_WRAPPER_STYLE)}
        side="top"
        sideOffset={5}
        style={{ zIndex: 9999 }}
      >
        <div className="text-xs">
          <div className="p-0.5">
            {defaultOlItems.map((item, index) => (
              <div
                key={index}
                className={classnames([
                  ...DROPDOWN_MENU_ITEM_STYLE,
                  item.icon === selectOL && isOLActive && 'bg-[#E5E5E5]',
                ])}
                onClick={() => {
                  selectOLCl(item);
                }}
              >
                <img width="20px" height="20px" src={item.icon} />
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
